import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PhoneInput, getFullPhoneNumber } from './PhoneInput';
import { MessageInput } from './MessageInput';
import { formatPhoneForWhatsApp } from '@/lib/phone-utils';
import { isValidPhoneNumber, getCountryCallingCode, CountryCode } from 'libphonenumber-js';

interface WhatsAppFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

const STORAGE_KEY = 'qr-whatsapp-draft';

export function WhatsAppForm({ contentData, onUpdate }: WhatsAppFormProps) {
  const [phone, setPhone] = useState(contentData.phone || '');
  const [countryCode, setCountryCode] = useState(contentData.countryCode || 'US');
  const [message, setMessage] = useState(contentData.message || '');

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.phone && !contentData.phone) setPhone(parsed.phone);
        if (parsed.countryCode) setCountryCode(parsed.countryCode);
        if (parsed.message && !contentData.message) setMessage(parsed.message);
      } catch {}
    }
  }, []);

  // Save to localStorage and update parent
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ phone, countryCode, message }));
    
    const fullPhone = formatPhoneForWhatsApp(phone, countryCode);
    onUpdate({
      phone: fullPhone,
      countryCode,
      message,
      rawPhone: phone,
    });
  }, [phone, countryCode, message, onUpdate]);

  // Check if phone is valid for test link
  const isPhoneValid = phone.length > 0 && (() => {
    try {
      const dialCode = getCountryCallingCode(countryCode as CountryCode);
      return isValidPhoneNumber(`+${dialCode}${phone}`);
    } catch {
      return false;
    }
  })();

  const handleTestLink = () => {
    if (!isPhoneValid) return;
    
    const fullPhone = formatPhoneForWhatsApp(phone, countryCode);
    const encodedMessage = encodeURIComponent(message);
    const url = message 
      ? `https://wa.me/${fullPhone}?text=${encodedMessage}`
      : `https://wa.me/${fullPhone}`;
    
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
        WhatsApp Message
      </h3>

      {/* Phone Input */}
      <PhoneInput
        value={phone}
        onChange={setPhone}
        countryCode={countryCode}
        onCountryChange={setCountryCode}
        label="Phone Number"
        required
        placeholder="Enter phone number"
      />

      {/* Message */}
      <MessageInput
        value={message}
        onChange={setMessage}
        label="Pre-filled Message (optional)"
        placeholder="Hello! I found you via QR code..."
        maxLength={500}
        showPreview
        previewTitle="WhatsApp Preview"
      />

      {/* Test Link Button */}
      <div className="pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleTestLink}
          disabled={!isPhoneValid}
          className="w-full gap-2"
        >
          <MessageCircle className="h-4 w-4" />
          Test WhatsApp Link
          <ExternalLink className="h-3 w-3 ml-auto" />
        </Button>
        {!isPhoneValid && phone.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Enter a valid phone number to test the link
          </p>
        )}
      </div>

      {/* Info */}
      <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
        <p className="font-medium mb-1">💡 How it works:</p>
        <p>
          When someone scans this QR code, it will open WhatsApp with the phone number
          and message pre-filled. They just need to tap send!
        </p>
      </div>
    </motion.div>
  );
}
